import React, { useState, useEffect, useContext } from 'react';
import { Card, Col, Row, Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import DatePicker from 'react-datepicker';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import PropTypes from 'prop-types';
import AppContext from 'context/Context';

import '../../../../../assets/scss/user.scss';
import 'react-datepicker/dist/react-datepicker.css';

import SectorTable from './SectorTable';
import TargetSettingModal from '../TargetSettingModal';
import MeasurableModal from '../MeasurableModal';

import { patch } from 'services/bs-sector-measurable/measurableService';
import { defaultFormatTime } from 'helpers/kpiHelper';
import { useSectorData } from 'hooks/useSectorData';

dayjs.extend(isoWeek);
dayjs.extend(require('dayjs/plugin/weekOfYear'));

export default function SectorCard({
  sector,
  setEditItem,
  setIsLoading,
  sheduleTypeText = 'Weekly',
  sheduleTypeId,
  isShowTodayParam = false
}) {
  const {
    config: { isDark }
  } = useContext(AppContext);
  const [isShowToday, setIsShowToday] = useState(isShowTodayParam);
  const [idEditing, setIdEditing] = useState(0);
  const [timesAgo, setTimesAgo] = useState([]);

  const defaultCol = sector?.scheduleTypeText === 'Daily' ? 5 : 4;
  const [noOfCol, setNoOfCol] = useState(defaultCol);
  const [selectedDate, setSelectedDate] = useState(new Date());

  const [selectedCurrentMonth, setSelectedCurrentMonth] = useState(
    dayjs().startOf('month').toDate()
  );
  const [selectedSubOneMonth, setSelectedSubOneMonth] = useState(
    dayjs().subtract(1, 'month').startOf('month').toDate()
  );

  const [isMesurableShow, setIsMesurableShow] = useState(false);
  const [mesurableData, setMesurableData] = useState({});

  const [isTargetSettingShow, setIsTargetSettingShow] = useState(false);
  const [targetSettingData, setTargetSettingData] = useState({});

  const typeMap = {
    Hourly: 'hour',
    Daily: 'day',
    Weekly: 'week',
    Monthly: 'month',
    Yearly: 'year'
  };

  const { measurables, reload } = useSectorData({
    sector,
    timesAgo,
    selectedCurrentMonth,
    selectedSubOneMonth,
    setIsLoading
  });

  // ===== EFFECT =====
  useEffect(() => {
    setIsShowToday(isShowTodayParam);
  }, [isShowTodayParam]);

  useEffect(() => {
    calculateTimesAgo(selectedDate, sheduleTypeText, noOfCol);

    setSelectedCurrentMonth(
      new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1)
    );

    setSelectedSubOneMonth(
      dayjs(selectedDate).subtract(1, 'month').startOf('month').toDate()
    );
  }, [selectedDate, sheduleTypeText, noOfCol, isShowToday]);

  useEffect(() => {
    if (sector) {
      setMesurableData({ sectorId: sector.id, kpiId: sector.kpiId });

      const noCol = sector.scheduleTypeText === 'Daily' ? 5 : 4;
      setNoOfCol(noCol);
    }
  }, [sector]);

  // ===== HANDLERS =====
  const handleSelectDate = date => setSelectedDate(date);

  const handleSaveUserIncharge = (option, id) => {
    patch(id, { inChargeUserId: option.userId }).then(() => {
      setIdEditing(0);
      reload();
    });
  };

  // ===== MODAL =====
  const toggleMesurabledModal = () => setIsMesurableShow(prev => !prev);

  const handleSubmitMesurableModal = () => {
    setIsMesurableShow(false);
    reload();
  };

  const toggleTargetSettingdModal = () => setIsTargetSettingShow(prev => !prev);

  const handleSubmitTargetSettingModal = () => {
    setIsTargetSettingShow(false);
    reload();
  };

  // ===== TIME LOGIC =====
  const calculateTimesAgo = (selectedDate, sheduleTypeText, noOfCol) => {
    const unit = typeMap[sheduleTypeText] || 'day';
    let times = [];

    if (unit === 'day') {
      let currentDate = dayjs(selectedDate).startOf('day');
      let dailyIndex = 0;

      if (isShowToday) {
        times.push({
          index: dailyIndex,
          date: currentDate.format(defaultFormatTime)
        });
      }

      for (let i = 0; i < noOfCol; ) {
        currentDate = currentDate.subtract(1, unit).startOf('day');

        if (currentDate.day() !== 0 && currentDate.day() !== 6) {
          times.push({
            index: dailyIndex + 1,
            date: currentDate.format(defaultFormatTime)
          });
          i++;
        }

        dailyIndex++;
      }

      times.reverse();
    } else {
      for (let i = noOfCol; i > 0; i--) {
        times.push({
          index: i,
          date: dayjs(selectedDate)
            .subtract(i, unit)
            .startOf(unit === 'week' ? 'isoWeek' : unit)
            .format(defaultFormatTime)
        });
      }
    }

    setTimesAgo(times);
  };

  const capitalizeFirstLetter = str => {
    const unit = typeMap[str] || 'day';
    return unit.charAt(0).toUpperCase() + unit.slice(1);
  };

  const calculateColName = date => {
    if (sheduleTypeText === 'Hourly') return dayjs(date).format('DD-MMM hA');

    if (sheduleTypeText === 'Monthly') return dayjs(date).format('MMM-YYYY');

    if (sheduleTypeText === 'Yearly') return dayjs(date).format('YYYY');

    return dayjs(date).format('DD-MMM');
  };

  // ===== RENDER =====
  return (
    <>
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <h3 className="me-2 mb-0">{sector?.name}</h3>

            <FontAwesomeIcon
              size="xs"
              icon={faPencilAlt}
              style={{ cursor: 'pointer', marginRight: '25px' }}
              onClick={() => setEditItem(sector)}
            />

            <Col sm={2} style={{ marginRight: '15px' }}>
              <Form.Select
                value={noOfCol}
                onChange={e => setNoOfCol(+e.target.value)}
              >
                {Array.from({ length: defaultCol }, (_, i) => i + 1).map(n => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col sm={3}>
              <DatePicker
                selected={selectedDate}
                dateFormat="dd/MM/yyyy"
                className="form-control"
                onChange={handleSelectDate}
              />
            </Col>
          </div>

          <Button onClick={() => {
            setMesurableData({ sectorId: sector.id, kpiId: sector.kpiId });
            setIsMesurableShow(true);
          }}>Create</Button>
        </Card.Header>

        <Card.Body>
          <Row>
            <Col sm={12}>
              <SectorTable
                measurables={measurables}
                timesAgo={timesAgo}
                calculateColName={calculateColName}
                capitalizeFirstLetter={capitalizeFirstLetter}
                sheduleTypeText={sheduleTypeText}
                selectedCurrentMonth={selectedCurrentMonth}
                selectedSubOneMonth={selectedSubOneMonth}
                idEditing={idEditing}
                setIdEditing={setIdEditing}
                handleSaveUserIncharge={handleSaveUserIncharge}
                toggleMesurabledModal={toggleMesurabledModal}
                setMesurableData={setMesurableData}
                toggleTargetSettingdModal={toggleTargetSettingdModal}
                setTargetSettingData={setTargetSettingData}
                sheduleTypeId={sheduleTypeId}
                getMeasurables={reload}
                noOfCol={noOfCol}
                isDark={isDark}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* MODALS */}
      {isMesurableShow && (
        <MeasurableModal
          isShow={isMesurableShow}
          handleClose={() => setIsMesurableShow(false)}
          data={mesurableData}
          handleSubmit={handleSubmitMesurableModal}
        />
      )}

      {isTargetSettingShow && (
        <TargetSettingModal
          isShow={isTargetSettingShow}
          handleClose={() => setIsTargetSettingShow(false)}
          data={targetSettingData}
          handleSubmit={handleSubmitTargetSettingModal}
        />
      )}
    </>
  );
}

SectorCard.propTypes = {
  sector: PropTypes.object,
  setEditItem: PropTypes.func,
  setIsLoading: PropTypes.func,
  sheduleTypeText: PropTypes.string,
  sheduleTypeId: PropTypes.number,
  isShowTodayParam: PropTypes.bool
};
